import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The design system's own font-size scale, declared so tailwind-merge can tell
 * a size from a colour.
 *
 * **This is not optional, and getting it wrong is invisible until it isn't.**
 * tailwind-merge drops earlier classes it believes conflict, and it infers a
 * class's group from the value: `text-small-label` and `text-text-muted` both
 * look like `text-<something-it-has-never-heard-of>`, so it filed them in the
 * same group and kept only the last one. Every `cn('text-small-label',
 * 'text-text-muted')` therefore silently lost its size and fell back to the
 * 18px root font — which is exactly what made a datagrid's column headers
 * render at two different sizes depending on whether the cell happened to also
 * set a colour.
 *
 * Listing the scale here fixes it everywhere at once, for the product's
 * components as much as admin's. A new token in `apps/web/tailwind.config.ts`
 * needs adding here too — the alternative is to keep discovering this one
 * component at a time.
 */
const FONT_SIZES = [
  'micro',
  'badge',
  'badge-lg',
  'tag',
  'tag-lg',
  'caption',
  'small-label',
  'ui',
  'ui-strong',
  'subtitle',
  'ui-lg',
  'heading-xs',
  'heading-sm',
  'panel-title',
  'page-title',
  'display-title',
  'card-title',
  'section-title',
  'body',
  'hero',
  'hero-display',
];

const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: FONT_SIZES }] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
